import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  FindingType,
  ScoreStateFilter,
  useGetSoftwareDetailsQuery,
} from "~/operations";
import { Link, Typography } from "@mui/material";
import { Home as HomeIcon } from "@mui/icons-material";
import { StatsProps } from "~/components/DetailsPage/components/Stats/Stats";
import { FlagOutlined, HealingOutlined } from "@mui/icons-material";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { SoftwareDetailsFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/types";
import { useMemo } from "react";

type UseSoftwareDetailsProps = {
  scope: SpaceOrWorkspaceScope;
};

type UseSoftwareDetailsReturn = {
  breadcrumbs: Array<React.ReactNode>;
  software: SoftwareDetailsFindingsNode | undefined;
  stats: StatsProps["stats"];
  state: {
    isLoading: boolean;
    hasError: boolean;
  };
  assetMrn: string;
  softwareMrn: string;
};

export function useSoftwareDetails({
  scope,
}: UseSoftwareDetailsProps): UseSoftwareDetailsReturn {
  const [searchParams] = useSearchParams();
  const assetMrn = searchParams.get("assetMrn") || "";
  const assetId = assetMrn.split("/").pop() || "";
  const softwareMrnParam = searchParams.get("softwareMrn") || "";

  const {
    data: packageDetailsData,
    loading: packageDetailsLoading,
    error: packageDetailsError,
  } = useGetSoftwareDetailsQuery({
    variables: {
      scopeMrn: assetMrn,
      filter: {
        types: [FindingType.Package],
        state: ScoreStateFilter.Open,
        mrn: softwareMrnParam,
      },
      first: 1,
    },
    skip: !softwareMrnParam || !assetMrn,
  });

  const software = useMemo(() => {
    const softwareFindingUnion =
      packageDetailsData?.findings?.__typename === "FindingsConnection"
        ? packageDetailsData?.findings
        : undefined;
    const firstSoftware = softwareFindingUnion?.edges?.at(0);
    return firstSoftware?.node?.__typename === "PackageFinding"
      ? firstSoftware.node
      : undefined;
  }, [packageDetailsData]);

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/inventory"
      component={RouterLink}
      to={`/space/inventory?${scope.params}`}
    >
      Inventory
    </Link>,
    <Link
      key="space/inventory/assetId"
      component={RouterLink}
      to={`/space/inventory/${assetId}/software?${scope.params}`}
    >
      {software?.asset?.name}
    </Link>,
    <Typography key="assetName">{software?.title}</Typography>,
  ];

  const stats: StatsProps["stats"] = [
    {
      label: "Installed",
      value: software?.installedVersion || "",
      icon: <FlagOutlined fontSize="inherit" />,
    },
    software?.availableVersion
      ? {
          label: "Available",
          value: software?.availableVersion,
          icon: <HealingOutlined fontSize="inherit" />,
        }
      : undefined,
  ].flatMap((f) => f ?? []);

  const softwareMrn = software?.mrn ?? "";

  return {
    assetMrn,
    software,
    breadcrumbs,
    stats,
    state: {
      isLoading: packageDetailsLoading,
      hasError: !!packageDetailsError,
    },
    softwareMrn,
  };
}
